import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { globals } from '../../config/globals';
import '/public/css/pages/story.css';
import '/public/css/components/apmGallerySlideshow.css';
import { Image } from '@apmg/mimas';
import { Time } from '@apmg/titan';
import { collectionLinkData } from '../../utils/utils';
import { fishForSocialMediaImage } from '../../components/Metatags/MetaTagHelpers';
import AudioPlayButton from '../../components/AudioPlayButton/AudioPlayButton';
import Content from '../../components/Content/Content';
import Metatags from '../../components/Metatags/Metatags';
import ShareSocialButtons from '../../components/ShareSocialButtons/ShareSocialButtons';
import { audioDownloadPrefix } from '../../utils/utils';
import GoogleAnalytics from '../../components/GoogleAnalytics/GoogleAnalytics';
import Transcript from '../../components/Transcript/Transcript';
import DonateAsk from '../../components/DonateAsk/DonateAsk';
import MnLegacy from '../../components/MnLegacy/MnLegacy';

const Story = ({
  data: { story, blacklist, donateAsk },
  minimal,
  rawHTML,
  useGA,
  wordcount,
}) => {
  // donateAsk is null if the config couldn't be loaded; DonateAsk renders
  // nothing without a title.
  const AppContext = React.createContext(
    donateAsk?.json ? JSON.parse(donateAsk.json) : {}
  );
  const redistributable = story?.primaryVisuals?.lead?.rights?.redistributable;
  const displayableImage =
    story?.primaryVisuals?.lead && !(minimal && !redistributable); //  an image exists and not a newspartner story with non-distributable image

  const socialMediaImage =
    story?.primaryVisuals?.lead ||
    (story?.primaryVisuals?.social && !(minimal && !redistributable)); //  an image exists and not a newspartner story with non-distributable image

  const audioSquareImage =
    story?.primaryVisuals?.lead?.aspect_ratios?.square?.instances[0]?.url;

  const img = fishForSocialMediaImage(story, socialMediaImage);
  const rssUrl = story?.collections?.find(
    (item) => item.rssUrl?.length
  )?.rssUrl;
  let authors;

  const DonateAskContext = () => {
    const context = useContext(AppContext);
    return <DonateAsk context={context} />;
  };

  if (story && story.contributors) {
    authors = story.contributors.map((contributor) => {
      return {
        title: `${contributor.profile?.title}`,
        href: `/people/[...slug]`,
        as: `/people/${contributor.profile?.canonicalSlug}`,
        order: `${contributor.order}`,
      };
    });
  }

  return (
    <>
      <Metatags
        title={story.title}
        seoHeadline={story.seoHeadline}
        shortTitle={story.shortTitle}
        fullSlug={`story/${story?.canonicalSlug}`}
        description={story.descriptionText}
        image={img?.url}
        imageHeight={img?.height}
        imageWidth={img?.width}
        imageAlt={story?.primaryVisuals?.social?.shortCaption}
        topic={story?.primaryCollection?.title}
        contentType="article"
        publishDate={story.publishDate}
        publishedTags={story.publishedTags}
        updatedAt={story.updatedPublishDate}
        authors={authors}
        originalSourceUrl={story.originalSourceUrl}
        noFollow={story.doNotCrawl}
        rssUrl={rssUrl}
        articleSection={story?.primaryCollection?.title ?? undefined}
        wordcount={wordcount}
      />
      <Content
        title={story.title}
        subtitle={story.subtitle}
        summary={story.summary}
        description={story.descriptionText}
        id={story.id}
        dateline={story.dateline}
        authors={authors}
        body={story.body}
        donateAsk={DonateAskContext}
        minimal={minimal}
        redistributable={redistributable}
        resourceType="story"
        contentUrl={`story/${story.canonicalSlug}`}
        shareButtons={
          !minimal && (
            <ShareSocialButtons
              contentUrl={`story/${story.canonicalSlug}`}
              title={story.title}
              contentType={story.resourceType}
              resourceType="story"
              id={story.id}
            />
          )
        }
        audio={story.primaryAudio}
        audioPlayButton={
          story.primaryAudio &&
          story.primaryAudio.encodings.length > 0 && (
            <AudioPlayButton
              audioSource={audioDownloadPrefix(
                story.primaryAudio.encodings[0].playFilePath
              )}
              audioTitle={story.primaryAudio.title}
              audioThumbnailSrc={audioSquareImage}
              audioThumbnailAlt={
                audioSquareImage && story.primaryVisuals?.lead?.shortCaption
              }
              label="Listen"
              elementClass="playButton-primary"
              showTitle={true}
            />
          )
        }
        image={
          displayableImage && (
            <Image
              key={story.primaryVisuals?.lead.fallback}
              image={story.primaryVisuals?.lead}
              sizes={globals.sizes?.primaryVisuals}
              alt={story.primaryVisuals?.lead?.shortCaption}
              fetchPriority="high"
              loading="eager"
            />
          )
        }
        imageCaption={story.primaryVisuals?.lead?.longCaption}
        imageCredit={story.primaryVisuals?.lead?.credit?.name}
        imageCreditHref={story.primaryVisuals?.lead?.credit?.url}
        video={story.primaryVisuals?.video}
        publishDate={
          <Time
            dateTime={story.publishDate}
            formatString="MMMM D, YYYY h:mm A"
          />
        }
        updatedPublishDate={
          story.updatedPublishDate && (
            <Time
              dateTime={story.updatedPublishDate}
              formatString="MMMM D, YYYY h:mm A"
            />
          )
        }
        publishedTags={story.publishedTags}
        embeddedAssets={story.embeddedAssets}
        tag={collectionLinkData(story.primaryCollection)}
        elementClass="story"
        useDonate={!minimal && true}
        primaryCollection={story.primaryCollection}
        relatedLinksBlacklist={
          blacklist?.json && JSON.parse(blacklist.json)?.slugs
        }
        rawHTML={rawHTML}
        transcript={<Transcript {...story.primaryAudio} />}
        collections={story.collections}
      />
      <MnLegacy collections={story.collections} />

      {minimal && (
        <div className="newspartners_notice">
          <p>
            This story originally appeared at:{' '}
            {`https://www.mprnews.org/story/${story.canonicalSlug}`}
          </p>
          <p>
            Questions or requests? Contact MPR News editor Michael Olson at
            newspartners@mpr.org © 2024 Minnesota Public Radio. All rights
            reserved.
          </p>
        </div>
      )}
      {useGA && <GoogleAnalytics />}
    </>
  );
};

Story.propTypes = {
  data: PropTypes.shape({
    blacklist: PropTypes.shape({
      json: PropTypes.string,
    }),
    donateAsk: PropTypes.object,
    story: PropTypes.shape({
      id: PropTypes.string,
      canonicalSlug: PropTypes.string,
      title: PropTypes.string,
      seoHeadline: PropTypes.string,
      shortTitle: PropTypes.string,
      subtitle: PropTypes.string,
      summary: PropTypes.string,
      dateline: PropTypes.string,
      doNotCrawl: PropTypes.bool,
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          href: PropTypes.string,
        })
      ),
      body: PropTypes.string,
      resourceType: PropTypes.string,
      contributors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
      descriptionText: PropTypes.string,
      image: PropTypes.element,
      imageCaption: PropTypes.string,
      imageCredit: PropTypes.string,
      imageCreditHref: PropTypes.string,
      primaryAudio: PropTypes.any,
      primaryCollection: PropTypes.any,
      collections: PropTypes.array,
      primaryVisuals: PropTypes.any,
      publishDate: PropTypes.string,
      publishedTags: PropTypes.array,
      updatedPublishDate: PropTypes.string,
      embeddedAssets: PropTypes.object,
      originalSourceUrl: PropTypes.string,
      tag: PropTypes.shape({
        tagName: PropTypes.string,
        to: PropTypes.string,
      }),
    }),
  }),
  minimal: PropTypes.bool,
  rawHTML: PropTypes.string,
  useGA: PropTypes.bool,
  wordcount: PropTypes.number,
};

export default Story;
